import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  answerLine,
  concludeInspection,
  deleteInspection,
  listQuality,
  moveInspection,
  moveNcr,
  raiseNcr,
  requestInspection,
  updateInspection,
  type NcrStatus,
  type QualityView,
} from '../api.js';
// Relative, not '@fitout/shared' — see the note at the top of `labels.ts`.
import { inspectionMachine } from '../../../../packages/shared/src/index.js';
import { AppLink } from '../link.js';
import { openFor, openForPhrase, roleLabel } from '../labels.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { CrossLink, focusClass, useArrivedToCreate, useFocusRow } from './cross-links.js';
import { useProjectMembers } from './ProjectMembers.js';
import { recordHref, sectionHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Checklist,
  ConcludeForm,
  EditForm,
  NcrForm,
  NcrMoveForm,
  RequestForm,
} from './QualityForms.js';
import { RowActions, type RowAction } from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';
import {
  INSPECTION_CLOSED,
  INSPECTION_LABELS,
  INSPECTION_SYMBOLS,
  NCR_LABELS,
  NCR_MOVES,
  NCR_SYMBOLS,
  SEVERITY_LABELS,
} from './quality-labels.js';

/**
 * Quality — inspections and NCRs (QA-01 to QA-05).
 *
 * The two registers share a screen because an NCR is usually the reason an
 * inspection failed, and reading one without the other tells half a story.
 *
 * **There is no way to say what an inspection's result was.** Finishing the
 * walk sends no result at all: a failed line is a failed inspection, the server
 * computes the outcome from the checklist, and the button says "Finish the
 * walk" rather than "Pass" or "Fail". If any line failed, the same form demands
 * an NCR before it will let go.
 */

interface Props {
  readonly projectId: string;
  /**
   * One inspection or one NCR, as its own page (§4.3).
   *
   * TWO REGISTERS SHARE THIS SCREEN, so this carries the register as well as
   * the id — `/projects/<id>/quality/inspections/<id>` and
   * `/projects/<id>/quality/ncrs/<id>`. It is the case the four-segment address
   * was designed for: a section holds more than one register, so an id on its
   * own cannot say which one it belongs to.
   *
   * BOTH EARN A PAGE, and this screen is where the drawer roll-out left the most
   * behind. An NCR carries its checks, its corrective action and its evidence —
   * and the evidence uploader was on EVERY ROW, which is the "hundred and forty
   * upload controls" the register pattern removed everywhere else and never got
   * to here. An inspection carries a checklist that expanded INSIDE the list and
   * pushed every inspection below it down the page, which is the other problem
   * the pattern exists to remove.
   */
  readonly record?: { readonly register: string; readonly id: string } | null;
  readonly can: {
    readonly request: boolean;
    readonly conduct: boolean;
    readonly raiseNcr: boolean;
    readonly editNcr: boolean;
  };
}

export function Quality({ projectId, record = null, can }: Props): JSX.Element {
  const [data, setData] = useState<QualityView | null>(null);
  /** Whether this screen has asked the server for every record. */
  const [wantAll, setWantAll] = useState(false);
  /** The page's one member fetch — its own when this renders standalone. */
  const people = useProjectMembers(projectId);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [raising, setRaising] = useState(false);
  const [walking, setWalking] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [concluding, setConcluding] = useState<string | null>(null);
  const [ncrAsking, setNcrAsking] = useState<{ id: string; to: NcrStatus } | null>(null);

  /**
   * `?new=ncr` and `?new=inspection` — arrived from the site home.
   *
   * Two registers share this screen, so it recognises two words. The forms are
   * still rendered behind `can.raiseNcr` and `can.request`, and the site home
   * does not offer the tile without them either — the URL opens a form nobody
   * was going to be refused.
   */
  const startNcr = useCallback(() => setRaising(true), []);
  const startInspection = useCallback(() => setRequesting(true), []);
  useArrivedToCreate('ncr', startNcr);
  useArrivedToCreate('inspection', startInspection);

  const load = useCallback(async () => {
    try {
      setData(await listQuality(projectId, wantAll ? LOAD_ALL_LIMIT : undefined));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the quality records.');
      setData({
        inspections: [],
        ncrs: [],
        inspections_total: 0,
        inspections_truncated: false,
        ncrs_total: 0,
        ncrs_truncated: false,
      });
    }
  }, [projectId, wantAll]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<QualityView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setData(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Move an inspection. Cancelling carries the reason the service demands. */
  function moveTo(id: string, to: string): void {
    setError(null);
    void run(() =>
      moveInspection(projectId, id, {
        status: to as 'scheduled',
        ...(to === 'cancelled' ? { reason: 'Cancelled from the register.' } : {}),
      }),
    );
  }

  /**
   * The inspection's own next step, which is what somebody repeats down the
   * register: schedule it, walk it, finish the walk.
   */
  function inspectionPrimary(i: QualityView['inspections'][number]): RowAction | null {
    // `can_conclude` and not the status alone: the matrix says the resource is
    // yours to edit, the MACHINE says whether you may sign the verdict, and
    // offering this on the first alone is a button the service then refuses.
    if (can.conduct && i.status === 'in_progress' && i.can_conclude) {
      return {
        label: 'Finish the walk',
        disabled: busy,
        onSelect: () => {
          setError(null);
          setConcluding(i.id);
        },
      };
    }
    const forward = can.conduct
      ? (['in_progress', 'scheduled'] as const).find((to) => i.allowed_next.includes(to))
      : undefined;
    if (forward) {
      return {
        label: forward === 'scheduled' ? 'Schedule' : 'Start the walk',
        disabled: busy,
        onSelect: () => moveTo(i.id, forward),
      };
    }
    if (i.lines.length > 0) {
      return {
        label: walking === i.id ? 'Hide the checklist' : 'Checklist',
        onSelect: () => setWalking(walking === i.id ? null : i.id),
      };
    }
    return null;
  }

  /**
   * WHY THERE IS NOTHING TO PRESS, WHEN THE APP LOOKS LIKE THERE SHOULD BE.
   *
   * Two things decide whether somebody can move an inspection on, and they are
   * not the same thing. The permission matrix says whether the resource is
   * theirs to edit — that is `can.conduct`, and it is what draws the checklist
   * and the buttons. The state machine says which ROLES may make each move, and
   * for an inspection that is the QA/QC inspector alone.
   *
   * A Company Admin holds `inspection: edit` and is named in none of the
   * transitions, so the app rendered a record with a checklist on it, no action
   * anywhere, and not a word about why. The owner sat on that page asking how
   * to click the lines. A refusal you can read is a feature; a dead page is a
   * bug.
   *
   * The roles come from the machine rather than a sentence typed here, so
   * widening a transition later cannot leave this text lying.
   */
  function noMoveExplanation(i: QualityView['inspections'][number]): JSX.Element | null {
    if (!can.conduct) return null;
    if (INSPECTION_CLOSED.includes(i.status)) return null;
    if (i.allowed_next.length > 0) return null;
    /*
     * AND `can_conclude`, which this first shipped without and got wrong.
     *
     * `allowed_next` never contains a verdict, so it is empty on EVERY
     * inspection being walked — including the inspector's own. The owner got
     * "you cannot take INS-0002 any further" sitting directly under a working
     * "Finish the walk" button, which is a worse failure than the silence it
     * replaced: the first said nothing, this contradicted itself.
     */
    if (i.can_conclude) return null;

    const who = [
      ...new Set(
        inspectionMachine.transitions
          .filter((t) => t.from === i.status && t.to !== 'cancelled')
          .flatMap((t) => t.by),
      ),
    ].map(roleLabel);
    if (who.length === 0) return null;

    return (
      <div className="alert is-caution" role="status">
        <span>
          <strong>You cannot take {i.number} any further.</strong> Only{' '}
          {who.length === 1 ? who[0] : `${who.slice(0, -1).join(', ')} or ${who.at(-1)}`} can
          schedule an inspection and walk it — quality sign-off belongs to the person doing the
          inspecting. Ask one of them, or have yourself added to this project in that role.
        </span>
      </div>
    );
  }

  /**
   * Everything that is not the one visible action.
   *
   * The checklist toggle comes OFF on the record page: the checklist is simply
   * there, so a control to reveal it would reveal what is already on screen.
   */
  function inspectionMore(
    i: QualityView['inspections'][number],
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = [];
    if (!onPage && i.lines.length > 0) {
      actions.push({
        label: walking === i.id ? 'Hide the checklist' : 'Checklist',
        onSelect: () => setWalking(walking === i.id ? null : i.id),
      });
    }
    if (can.conduct) {
      for (const to of i.allowed_next) {
        actions.push({
          label:
            to === 'scheduled' ? 'Schedule' : to === 'in_progress' ? 'Start the walk' : 'Cancel',
          danger: to === 'cancelled',
          disabled: busy,
          onSelect: () => moveTo(i.id, to),
        });
      }
    }
    // Naming the inspector, the date, the notes and — before the walk starts —
    // the checklist. Closed inspections are not offered it: the service refuses
    // them, and an action that always fails is worse than no action.
    if (can.conduct && !INSPECTION_CLOSED.includes(i.status)) {
      actions.push({
        label: 'Edit',
        disabled: busy,
        onSelect: () => {
          setError(null);
          setEditing(i.id);
        },
      });
    }
    if (can.conduct && i.status === 'requested') {
      actions.push({
        label: 'Delete',
        danger: true,
        disabled: busy,
        onSelect: () => {
          void run(() => deleteInspection(projectId, i.id)).then((ok) => {
            // Deleting the record you are standing on leaves you on an address
            // that names nothing. The register is where the deletion makes
            // sense, so that is where it puts you.
            if (ok && onPage) navigate(sectionHref(projectId, 'quality'));
          });
        },
      });
    }
    return actions.filter((action) => action.label !== primary?.label);
  }

  /** Move an NCR, asking for words first wherever the record needs them. */
  function moveNcrTo(id: string, to: NcrStatus): void {
    setError(null);
    // Everything except a straight verify needs words.
    if (to === 'verified') {
      void run(() => moveNcr(projectId, id, { status: to }));
      return;
    }
    setNcrAsking({ id, to });
  }

  /**
   * The NCR's own next step: assign the fix, mark it done, check and close.
   *
   * `void` is absent — it is an ending that demands a reason, and a register
   * whose one-click action voids an NCR is a register that voids them by
   * accident.
   */
  function ncrPrimary(n: QualityView['ncrs'][number]): RowAction | null {
    if (!can.editNcr) return null;
    const forward = (['corrective_action_assigned', 'work_complete', 'verified'] as const).find(
      (to) => n.allowed_next.includes(to),
    );
    return forward
      ? {
          label: NCR_MOVES[forward] ?? forward,
          disabled: busy,
          onSelect: () => moveNcrTo(n.id, forward),
        }
      : null;
  }

  /** Everything that is not the row's one visible action. */
  function ncrMore(
    n: QualityView['ncrs'][number],
    primary: RowAction | null,
  ): readonly RowAction[] {
    if (!can.editNcr) return [];
    return n.allowed_next
      .map(
        (to) =>
          ({
            label: NCR_MOVES[to] ?? to,
            danger: to === 'void',
            disabled: busy,
            onSelect: () => moveNcrTo(n.id, to),
          }) satisfies RowAction,
      )
      .filter((action) => action.label !== primary?.label);
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Quality</h3>
      {data && !requesting && can.request && (
        <button type="button" className="inline" onClick={() => setRequesting(true)}>
          Request an inspection
        </button>
      )}
      {data && !raising && can.raiseNcr && (
        <button type="button" className="inline secondary" onClick={() => setRaising(true)}>
          Raise an NCR
        </button>
      )}
    </div>
  );

  /*
   * The same four states on a record page, still four different sentences. The
   * furniture above them changes: the register's head carries two "raise
   * something" buttons, and neither is the right thing to offer somebody who
   * followed a link to one particular inspection or NCR.
   */
  const above = record ? <RecordBack projectId={projectId} register={record.register} /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s quality records.</p>
      </>
    );
  }

  if (data === null) {
    return (
      <>
        {above}
        <p aria-live="polite">
          Loading {record?.register === 'ncrs' ? 'the NCR' : record ? 'the inspection' : 'the quality records'}…
        </p>
      </>
    );
  }

  const openInspections = data.inspections.filter(
    (i) => !['passed', 'passed_with_observations', 'failed', 'cancelled'].includes(i.status),
  );
  const openNcrs = data.ncrs.filter((n) => !['verified', 'void'].includes(n.status));
  const seriousNcrs = openNcrs.filter((n) => n.severity === 'high' || n.severity === 'critical');

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  /**
   * Only part of a register is loaded.
   *
   * This screen has no toolbar to hang the usual notice on, and it needs one
   * MORE than the screens that do: the counts above — open inspections, open
   * NCRs, serious ones — are worked out from what is loaded. On a truncated
   * load "2 serious NCRs" would be a confident number about a subset, on the
   * one screen where a missed NCR is a quality failure nobody answered for.
   */
  const partial = data.inspections_truncated || data.ncrs_truncated;
  const partialNotice = partial && (
    <p className="alert" role="alert">
      <strong>
        Only part of this is loaded — {data.inspections.length} of {data.inspections_total}{' '}
        inspections and {data.ncrs.length} of {data.ncrs_total} NCRs.
      </strong>{' '}
      The counts below are worked out from what is loaded, so they are lower than the truth.{' '}
      <button
        type="button"
        className="link-button"
        disabled={wantAll}
        onClick={() => setWantAll(true)}
      >
        {wantAll ? 'Loading…' : 'Load the rest'}
      </button>
    </p>
  );

  /**
   * The two forms a record page needs, named so they can be SHARED rather than
   * copied — and so each stays where it belongs on the register.
   *
   * They are not moved into one block at the bottom: on the register the
   * conclude form belongs under the inspections it acts on, and the NCR move
   * form under the NCRs. A shared block would put the conclude form below the
   * whole NCR list, which is a long way from the walk it is finishing.
   *
   * `05` is why both exist at all: finishing a walk with a failed line demands
   * an NCR in the same transaction, and every NCR move except a straight verify
   * demands words. Neither rule may be copied.
   */
  const concludeForm = concluding && (
    <ConcludeForm
      inspection={data.inspections.find((i) => i.id === concluding)!}
      people={people}
      busy={busy}
      onCancel={() => {
        setConcluding(null);
        setError(null);
      }}
      onSubmit={async (input) => {
        if (await run(() => concludeInspection(projectId, concluding, input))) {
          setConcluding(null);
        }
      }}
    />
  );

  /*
   * Naming the inspector, the date, the notes and the checklist.
   *
   * Guarded on the record being FOUND rather than asserted like the two around
   * it, because the edit survives a reload the others do not: `onWrite` now
   * refetches under the open form, and an inspection that has just been
   * cancelled or deleted in another tab would take the whole screen down with
   * a non-null assertion.
   */
  const editTarget = editing ? data.inspections.find((i) => i.id === editing) : undefined;
  const editForm = editTarget && (
    <EditForm
      inspection={editTarget}
      people={people}
      busy={busy}
      onCancel={() => {
        setEditing(null);
        setError(null);
      }}
      onSubmit={async (input) => {
        if (await run(() => updateInspection(projectId, editTarget.id, input))) {
          setEditing(null);
        }
      }}
    />
  );

  const ncrMoveForm = ncrAsking && (
    <NcrMoveForm
      ncr={data.ncrs.find((n) => n.id === ncrAsking.id)!}
      to={ncrAsking.to}
      people={people}
      busy={busy}
      onCancel={() => {
        setNcrAsking(null);
        setError(null);
      }}
      onSubmit={async (input) => {
        if (await run(() => moveNcr(projectId, ncrAsking.id, { status: ncrAsking.to, ...input })))
          setNcrAsking(null);
      }}
    />
  );

  /** Both, for a record page — where there is only one record to be near. */
  const forms = (
    <>
      {concludeForm}
      {ncrMoveForm}
    </>
  );

  /** The NCRs that came out of one inspection, resolved from their numbers. */
  function ncrsFrom(numbers: readonly string[]): QualityView['ncrs'] {
    return numbers
      .map((number) => data?.ncrs.find((n) => n.number === number))
      .filter((n): n is QualityView['ncrs'][number] => n !== undefined);
  }

  // ----------------------------------------------------- one inspection, in full
  if (record?.register === 'inspections') {
    const found = data.inspections.find((i) => i.id === record.id) ?? null;
    if (!found) {
      /* A FAILED LOAD IS NOT A MISSING RECORD — see `Materials.tsx`. */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="inspections" />
      );
    }

    /*
     * `inspectionPrimary` falls back to the checklist TOGGLE once an inspection
     * has nowhere forward to go. On the page the checklist is already on screen,
     * so that button would hide what somebody came to read — it is dropped, and
     * a finished inspection's page carries only its More menu.
     */
    const forward = inspectionPrimary(found);
    const primary = forward && /checklist/i.test(forward.label) ? null : forward;
    /*
     * THE BAR IS GUARDED ON BOTH, and guarding it on the primary alone was a
     * real bug: an inspection whose only remaining move is Cancel has no
     * primary — `inspectionPrimary` only ever offers a FORWARD move — and the
     * whole bar disappeared, taking the one move it had with it. A reader who
     * may do nothing gets no bar; a reader who may do something always gets it.
     */
    const more = inspectionMore(found, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={found.number} {...(primary ? { primary } : {})} more={more} />
      ) : null;
    const raised = ncrsFrom(found.ncr_numbers);

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="inspections"
          reference={found.number}
          title={found.title}
          status={INSPECTION_LABELS[found.status]}
          meta={
            <>
              {found.inspector_name ?? 'No inspector named'}
              {found.location_path && <> · {found.location_path}</>}
              {found.failed_lines > 0 && (
                <span className="task-overdue">
                  {' · '}
                  {found.failed_lines} failed {found.failed_lines === 1 ? 'line' : 'lines'}
                </span>
              )}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<InspectionProperties inspection={found} />}
        >
          {editForm}
          {noMoveExplanation(found)}
          {/*
            THE CHECKLIST IS THE RECORD, so on a page it is simply THERE. On the
            register it is behind a toggle, and it has to be: expanded in the
            list it pushes every inspection below it down the page. That toggle
            was the row's one action for a finished inspection — which is a
            register whose primary act is "show me the thing", and the page is
            the honest answer to that.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">The checklist</h4>
            </div>
            {found.lines.length > 0 ? (
              <Checklist
                inspection={found}
                projectId={projectId}
                busy={busy}
                editable={can.conduct && found.status === 'in_progress'}
                onAnswer={(lineId, result, note) =>
                  void run(() => answerLine(projectId, found.id, lineId, { result, note }))
                }
              />
            ) : (
              /*
               * An empty state must name an action ITS reader can take. The
               * checklist comes from the form template the inspection was
               * requested against, so there is nothing to add here — it says
               * where the lines come from instead of offering a button.
               */
              <p className="hint">
                This inspection has no checklist lines. They come from the form template it was
                requested against, which is set when the inspection is requested.
              </p>
            )}
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">NCRs raised from it</h4>
            </div>
            {found.ncr_numbers.length > 0 ? (
              <p className="hint">
                {found.ncr_numbers.map((number, index) => {
                  const ncr = raised.find((n) => n.number === number);
                  return (
                    <span key={number}>
                      {index > 0 && ', '}
                      {/*
                        A number that matches nothing still prints. An NCR this
                        caller may not read is not an NCR that did not happen.
                      */}
                      {ncr ? (
                        <CrossLink
                          projectId={projectId}
                          section="quality"
                          register="ncrs"
                          focus={ncr.id}
                          says="the NCR raised from this inspection"
                        >
                          {number}
                        </CrossLink>
                      ) : (
                        number
                      )}
                    </span>
                  );
                })}
              </p>
            ) : (
              <p className="hint">
                Nothing was raised from this walk. A failed line fails the whole inspection and
                cannot be closed without an NCR, so an inspection with none either passed or has
                not been walked yet.
              </p>
            )}
          </div>
        </RecordPage>
        {forms}
      </>
    );
  }

  // ------------------------------------------------------------ one NCR, in full
  if (record?.register === 'ncrs') {
    const found = data.ncrs.find((n) => n.id === record.id) ?? null;
    if (!found) {
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="ncrs" />
      );
    }

    const primary = ncrPrimary(found);
    /*
     * Guarded on BOTH, for the same reason as the inspection above. `ncrPrimary`
     * only ever offers a forward move, so an NCR whose one remaining move is
     * Void has no primary — and guarding the bar on the primary alone took the
     * Void away with it.
     */
    const more = ncrMore(found, primary);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={found.number} {...(primary ? { primary } : {})} more={more} />
      ) : null;

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="ncrs"
          reference={found.number}
          title={found.description}
          status={NCR_LABELS[found.status]}
          meta={
            <>
              {SEVERITY_LABELS[found.severity]}
              {found.assignee_name && <> · {found.assignee_name}</>}
              {' · '}
              {openForPhrase(found.days_open)}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<NcrProperties ncr={found} projectId={projectId} />}
        >
          {/*
            THE EVIDENCE, AND IT IS WHY THIS RECORD NEEDED A PAGE. The uploader
            was on every NCR row — the "hundred and forty upload controls" the
            register pattern removed everywhere else and never reached here.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Evidence</h4>
            </div>
            {/*
              An NCR closed on a sentence alone is a claim; a photo or a test
              certificate is the thing a consultant will ask for six months
              later. INTERNAL by default, unlike a snag's: an NCR is the
              contractor's own quality record, and a client sees what was
              deliberately shared.
            */}
            <Attachments
              projectId={projectId}
              entityType="ncr"
              entityId={found.id}
              can={{ write: can.editNcr }}
              noun="evidence"
              category="certificate"
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Checks</h4>
            </div>
            {found.verifications.length > 0 ? (
              <ul className="snag-checks">
                {found.verifications.map((v) => (
                  <li key={v.id}>
                    <strong>{v.outcome === 'passed' ? 'Checked' : 'Sent back'}</strong> by{' '}
                    {v.verifier_name ?? 'someone'}
                    {v.comment ? ` — ${v.comment}` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              /*
               * It cannot name "check it" as the action: an assignee may never
               * verify their own NCR and the server refuses it, not even for a
               * Company Admin. So it says what has to happen first and who does
               * it.
               */
              <p className="hint">
                Nobody has checked this yet. The fix is marked done by whoever did it and then
                checked by somebody else — never by the same person.
              </p>
            )}
          </div>
        </RecordPage>
        {forms}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}
      {partialNotice}

      {requesting && (
        <RequestForm
          busy={busy}
          onCancel={() => {
            setRequesting(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => requestInspection(projectId, input))) setRequesting(false);
          }}
        />
      )}

      {raising && (
        <NcrForm
          people={people}
          busy={busy}
          onCancel={() => {
            setRaising(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => raiseNcr(projectId, input))) setRaising(false);
          }}
        />
      )}

      {(data.inspections.length > 0 || data.ncrs.length > 0) && (
        <p className="hint" role="status">
          {/* "1 inspections" is how a screen tells you nobody read it. */}
          <strong>
            {openInspections.length} {openInspections.length === 1 ? 'inspection' : 'inspections'}{' '}
            still to close
          </strong>
          {' · '}
          {openNcrs.length} {openNcrs.length === 1 ? 'NCR' : 'NCRs'} open
          {seriousNcrs.length > 0 && (
            <>
              {' · '}
              <span className="task-overdue">{seriousNcrs.length} of them serious</span>
            </>
          )}
          . The QA/QC stage needs every inspection closed and no serious NCR left open.
        </p>
      )}

      {/* ------------------------------------------------------ inspections */}
      <h4 className="sub-head">Inspections</h4>

      {data.inspections.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No inspections yet.</strong>
          </p>
          <p>
            An inspection is a checklist walked on site. Any line marked Fail fails the whole
            inspection — that is not something anybody can override — and a failed inspection has
            to have an NCR raised against it.
          </p>
        </div>
      ) : (
        <ul className="quality-list">
          {data.inspections.map((i) => (
            <QualityItem key={i.id} recordId={i.id} className={`ins-${i.status}`}>
              <div className="quality-row">
                <div className="quality-what">
                  <span className="quality-title">
                    <span className="ms-symbol" aria-hidden="true">
                      {INSPECTION_SYMBOLS[i.status]}
                    </span>{' '}
                    {/*
                      The reference is the way IN to the record, and it is a real
                      link because the record has an address now — so a
                      middle-click, a new tab and a copied link all mean what
                      they look like they mean.
                    */}
                    <AppLink
                      className="rk-open"
                      to={recordHref(projectId, 'inspections', i.id)}
                    >
                      <strong>{i.number}</strong> {i.title}
                      <span className="visually-hidden"> — open this inspection</span>
                    </AppLink>
                  </span>
                  <span className="tree-code">
                    {INSPECTION_LABELS[i.status]}
                    {i.inspector_name ? ` · ${i.inspector_name}` : ''}
                    {i.location_path ? ` · ${i.location_path}` : ''}
                  </span>
                  {i.lines.length > 0 && (
                    <span className="tree-code">
                      {i.lines.length} {i.lines.length === 1 ? 'line' : 'lines'}
                      {i.unanswered > 0 && ` · ${i.unanswered} unanswered`}
                      {i.failed_lines > 0 && (
                        <span className="task-overdue"> · {i.failed_lines} failed</span>
                      )}
                    </span>
                  )}
                  {/*
                    The NCRs this inspection produced, as links. Both records
                    are already on this screen, so the id is resolved from the
                    number rather than fetched — and a number that matches
                    nothing still prints, because an NCR the caller may not read
                    is not an NCR that did not happen.
                  */}
                  {i.ncr_numbers.length > 0 && (
                    <span className="quality-note">
                      Raised{' '}
                      {i.ncr_numbers.map((number, index) => {
                        const ncr = data.ncrs.find((n) => n.number === number);
                        return (
                          <span key={number}>
                            {index > 0 && ', '}
                            {ncr ? (
                              <CrossLink
                                projectId={projectId}
                                section="quality"
                                focus={ncr.id}
                                says="the NCR raised from this inspection"
                              >
                                {number}
                              </CrossLink>
                            ) : (
                              number
                            )}
                          </span>
                        );
                      })}
                    </span>
                  )}
                  {i.notes && <span className="quality-note">{i.notes}</span>}
                </div>
                <div className="row-actions">
                  {/*
                    This row carried up to seven controls — the checklist
                    toggle, every move the machine allowed, finishing the walk
                    and deleting. One of them is the act somebody repeats down
                    the register, and the rest are behind the menu now.
                  */}
                  {(() => {
                    const primary = inspectionPrimary(i);
                    return (
                      <RowActions
                        rowLabel={i.number}
                        {...(primary ? { primary } : {})}
                        more={inspectionMore(i, primary)}
                      />
                    );
                  })()}
                </div>
              </div>

              {editing === i.id && editForm}

              {walking === i.id && (
                <Checklist
                  inspection={i}
                  projectId={projectId}
                  busy={busy}
                  editable={can.conduct && i.status === 'in_progress'}
                  onAnswer={(lineId, result, note) =>
                    void run(() => answerLine(projectId, i.id, lineId, { result, note }))
                  }
                />
              )}

              {/*
                Under the row it is about, not once at the top: on a register of
                twenty this has to say WHICH inspection is stuck, and standing
                under it is how it says so without repeating the number.
              */}
              {walking === i.id && noMoveExplanation(i)}
            </QualityItem>
          ))}
        </ul>
      )}

      {concludeForm}

      {/* ------------------------------------------------------------- NCRs */}
      <h4 className="sub-head">Non-conformance reports</h4>

      {data.ncrs.length === 0 ? (
        <p className="hint">None raised.</p>
      ) : (
        <ul className="quality-list">
          {data.ncrs.map((n) => (
            <QualityItem key={n.id} recordId={n.id} className={`ncr-${n.status} sev-${n.severity}`}>
              <div className="quality-row">
                <div className="quality-what">
                  <span className="quality-title">
                    <span className="ms-symbol" aria-hidden="true">
                      {NCR_SYMBOLS[n.status]}
                    </span>{' '}
                    <AppLink className="rk-open" to={recordHref(projectId, 'ncrs', n.id)}>
                      <strong>{n.number}</strong> {n.description}
                      <span className="visually-hidden"> — open this NCR</span>
                    </AppLink>
                  </span>
                  <span className="tree-code">
                    {SEVERITY_LABELS[n.severity]} · {NCR_LABELS[n.status]}
                    {n.assignee_name ? ` · ${n.assignee_name}` : ''}
                    {/* Back to the walk it came out of. */}
                    {n.inspection_number && (
                      <>
                        {' · from '}
                        {n.inspection_id ? (
                          <CrossLink
                            projectId={projectId}
                            section="quality"
                            focus={n.inspection_id}
                            says="the inspection this NCR came out of"
                          >
                            {n.inspection_number}
                          </CrossLink>
                        ) : (
                          n.inspection_number
                        )}
                      </>
                    )}
                    {` · ${openForPhrase(n.days_open)}`}
                  </span>
                  {n.corrective_action && (
                    <span className="quality-note">To do: {n.corrective_action}</span>
                  )}
                  {n.completion_note && (
                    <span className="quality-note">Done: {n.completion_note}</span>
                  )}
                  {n.void_reason && <span className="quality-note">Void: {n.void_reason}</span>}
                  {n.verifications.map((v) => (
                    <span className="snag-check" key={v.id}>
                      {v.outcome === 'passed' ? 'Checked' : 'Sent back'} by{' '}
                      {v.verifier_name ?? 'someone'}
                      {v.comment ? ` — ${v.comment}` : ''}
                    </span>
                  ))}
                  {/*
                    THE EVIDENCE MOVED TO THE RECORD'S PAGE (§4.3), and this was
                    the last place in the product where an uploader sat on every
                    row. A populated job has thirty NCRs, which was thirty file
                    pickers on one screen — the noise the register pattern
                    removed everywhere else and never reached here.

                    The row says NOTHING about how many files there are, and
                    that is deliberate: `listQuality` does not return a count,
                    and a number invented in the browser is a claim the server
                    never made. Every other register does the same — a material
                    row does not count its certificates either.
                  */}
                </div>
                <div className="row-actions">
                  {/*
                    The NCR's own next step goes on the row — assign the fix,
                    mark it done, check and close. Voiding is an ending and it
                    needs words, so it stays in the menu, like every other
                    register's.
                  */}
                  {(() => {
                    const primary = ncrPrimary(n);
                    return (
                      <RowActions
                        rowLabel={n.number}
                        {...(primary ? { primary } : {})}
                        more={ncrMore(n, primary)}
                      />
                    );
                  })()}
                </div>
              </div>
            </QualityItem>
          ))}
        </ul>
      )}

      {ncrMoveForm}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * One inspection or NCR, which knows whether a cross-link came looking for it.
 *
 * Both registers are on this screen and they point at each other, so a link
 * from an inspection to its NCR often lands a few rows further down the same
 * page — which is exactly when a highlight earns its keep.
 */
function QualityItem({
  recordId,
  className,
  children,
}: {
  readonly recordId: string;
  readonly className: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLLIElement>(recordId);
  return (
    <li ref={ref} className={focusClass(focused, className)} aria-current={focused ? 'true' : undefined}>
      {children}
    </li>
  );
}


// ---------------------------------------------------------------------------

/**
 * What the inspection IS — the record page's rail.
 *
 * The checklist and the NCRs raised from it are in the main column, because
 * both are LISTS and both are what the record is about. The facts here are the
 * ones that never had a home: the register row could only carry three of them
 * before it stopped being a row.
 */
function InspectionProperties({
  inspection,
}: {
  readonly inspection: QualityView['inspections'][number];
}): JSX.Element {
  return (
    <dl className="detail-grid">
      <dt>Where it is</dt>
      <dd>{INSPECTION_LABELS[inspection.status]}</dd>
      <dt>Inspector</dt>
      <dd>{inspection.inspector_name ?? '—'}</dd>
      <dt>Where</dt>
      <dd>{inspection.location_path ?? '—'}</dd>
      <dt>Lines</dt>
      <dd>
        {inspection.lines.length}
        {inspection.unanswered > 0 && (
          <span className="tree-code"> · {inspection.unanswered} unanswered</span>
        )}
        {/*
          A FAILED LINE FAILS THE WHOLE INSPECTION and nobody can override it
          (`05` §4), so the count is a fact about the outcome rather than a
          detail of it. Said in words; the class only ever agrees with them.
        */}
        {inspection.failed_lines > 0 && (
          <span className="task-overdue"> · {inspection.failed_lines} failed</span>
        )}
      </dd>
      {inspection.notes && (
        <>
          <dt>Notes</dt>
          <dd>{inspection.notes}</dd>
        </>
      )}
    </dl>
  );
}

/**
 * What the NCR IS — the record page's rail.
 *
 * The evidence and the checks are in the main column. Everything here used to be
 * squeezed onto the row as `tree-code` and `quality-note` lines, which is how a
 * list item ends up eleven lines tall.
 */
function NcrProperties({
  ncr,
  projectId,
}: {
  readonly ncr: QualityView['ncrs'][number];
  readonly projectId: string;
}): JSX.Element {
  return (
    <dl className="detail-grid">
      <dt>Severity</dt>
      <dd>{SEVERITY_LABELS[ncr.severity]}</dd>
      <dt>With</dt>
      <dd>{ncr.assignee_name ?? '—'}</dd>
      <dt>Open for</dt>
      <dd>
        {openFor(ncr.days_open)}
      </dd>
      <dt>Came out of</dt>
      <dd>
        {ncr.inspection_number ? (
          ncr.inspection_id ? (
            <CrossLink
              projectId={projectId}
              section="quality"
              register="inspections"
              focus={ncr.inspection_id}
              says="the inspection this NCR came out of"
            >
              {ncr.inspection_number}
            </CrossLink>
          ) : (
            ncr.inspection_number
          )
        ) : (
          /* An NCR can be raised on its own, without a walk behind it. */
          'Raised on its own'
        )}
      </dd>
      {ncr.corrective_action && (
        <>
          <dt>To do</dt>
          <dd>{ncr.corrective_action}</dd>
        </>
      )}
      {ncr.completion_note && (
        <>
          <dt>Done</dt>
          <dd>{ncr.completion_note}</dd>
        </>
      )}
      {ncr.void_reason && (
        <>
          <dt>Voided because</dt>
          <dd>{ncr.void_reason}</dd>
        </>
      )}
    </dl>
  );
}
